
import React, { createContext, useContext, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { useAuthProvider } from '@/hooks/useAuthProvider';
import { useAuthMethods } from '@/hooks/auth';

// Define the possible return type for the signUp function
export type SignUpResult = boolean | {
  success: boolean;
  requiresEmailConfirmation: boolean;
  userId: string;
  email: string;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<boolean>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<SignUpResult>;
  signInWithGoogle: () => Promise<boolean>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { 
    user, 
    session, 
    isLoading, 
    isAdmin,
    setUser,
    setSession,
    setIsAdmin,
    setIsLoading
  } = useAuthProvider();

  const { signIn, signUp, signInWithGoogle, signOut } = useAuthMethods({
    setIsLoading,
    setUser,
    setSession,
    setIsAdmin
  });

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      isLoading, 
      signIn, 
      signUp, 
      signInWithGoogle, 
      signOut, 
      isAdmin 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
